import React, { useEffect, useRef, useState } from 'react'
import { Button, Card, Col, DatePicker, Form, Row, Spin, Table } from 'antd'
import BaseListing from 'src/BaseListings'
import { wtApiCall } from 'src/helpers/commons/ApiWrapper'
import { lzNotifyErrorMessageFromResult } from 'src/helpers/commons/notifications'
import { wtFormatRoundedValue, wtRoundValue } from 'src/helpers/commons/utils'
import WidgetBranchSelector from 'src/widgets/Selectors/WidgetBranchSelector'
import WidgetItemBrandSelector from 'src/widgets/Selectors/WidgetItemBrandSelector'
import WidgetItemSelector from 'src/widgets/Selectors/WidgetItemSelector'
import WidgetProjectSelector from 'src/widgets/Selectors/WidgetProjectSelector'
import WidgetVendorSelector from 'src/widgets/Selectors/WidgetVendorSelector'
import { wtGetFromLocalStorage } from 'src/helpers/commons/LocalStorage'
import { useDownloadExcel } from 'react-export-table-to-excel'

const GrnReport = () => {
  const [form] = Form.useForm()
  const tableRef = useRef(null)
  const baseRef = useRef(new BaseListing('stock_report'))
  const Base = baseRef.current

  const [isLoading, setIsLoading] = useState(false)
  const [reportData, setReportData] = useState([])
  const [dateRange, setDateRange] = useState([])
  const [branches] = useState(wtGetFromLocalStorage('branches') || [])
  const [branchId, setBranchId] = useState(0)
  const [branchName, setBranchName] = useState('')
  const [projects, setProjects] = useState([])
  const [projectId, setProjectId] = useState(0)
  const [projectName, setProjectName] = useState('')
  const [vendors, setVendors] = useState([])
  const [vendorId, setVendorId] = useState(0)
  const [vendorName, setVendorName] = useState('')
  const [items, setItems] = useState([])
  const [itemId, setItemId] = useState(0)
  const [itemName, setItemName] = useState('')
  const [brands, setBrands] = useState([])
  const [brandId, setBrandId] = useState(0)
  const [brandName, setBrandName] = useState('')

  useEffect(() => {
    baseRef.current.dropdownData(
      'projects,vendors,items,item_brands',
      {},
      [setProjects, setVendors, setItems, setBrands],
      1,
    )
  }, [])

  const fetchReport = () => {
    if (!dateRange || dateRange.length < 2) {
      lzNotifyErrorMessageFromResult('Please select date range')
      return
    }

    setIsLoading(true)
    wtApiCall(
      'reports/grn_report',
      {
        from_date: dateRange[0].format('YYYY-MM-DD'),
        to_date: dateRange[1].format('YYYY-MM-DD'),
        branch_id: branchId,
        project_id: projectId,
        vendor_id: vendorId,
        item_id: itemId,
        brand_id: brandId,
      },
      'get',
      (response) => {
        setReportData(response?.data || [])
        setIsLoading(false)
      },
      (error) => {
        lzNotifyErrorMessageFromResult(error)
        setIsLoading(false)
      },
    )
  }

  const totals = reportData.reduce(
    (acc, row) => {
      acc.total += Number(row.total || 0)
      acc.tax_amount += Number(row.tax_amount || 0)
      acc.net_total += Number(row.net_total || 0)
      return acc
    },
    { total: 0, tax_amount: 0, net_total: 0 },
  )

  const columns = [
    {
      title: 'SR',
      width: 60,
      fixed: 'left',
      render: (_, __, index) => index + 1,
    },
    {
      title: 'Branch',
      dataIndex: ['branch', 'name'],
      width: 180,
      fixed: 'left',
    },
    {
      title: 'GR No',
      dataIndex: ['goods_received_note', 'code'],
      width: 130,
      fixed: 'left',
      render: (_, record) => (
        <span
          onClick={() =>
            window.open(`/goods_recevied_note#view:${record.goods_received_note?.id}`, '_blank')
          }
          style={{ color: 'blue', cursor: 'pointer' }}
        >
          {record.goods_received_note?.code || ''}
        </span>
      ),
    },
    {
      title: 'PO No',
      dataIndex: ['purchase_order', 'code'],
      width: 130,
      render: (_, record) => (
        <span
          onClick={() => window.open(`/purchase_order#view:${record.purchase_order?.id}`, '_blank')}
          style={{ color: 'blue', cursor: 'pointer' }}
        >
          {record.purchase_order?.code || ''}
        </span>
      ),
    },
    {
      title: 'LC Number',
      dataIndex: ['purchase_order', 'lc_no'],
      width: 140,
    },
    {
      title: 'GRN Date',
      dataIndex: ['goods_received_note', 'received_date'],
      width: 120,
    },
    {
      title: 'Supplier',
      dataIndex: ['supplier', 'name'],
      width: 220,
    },
    {
      title: 'Project',
      dataIndex: ['project', 'name'],
      width: 220,
    },
    {
      title: 'Item Name',
      dataIndex: ['item', 'name'],
      width: 220,
    },
    {
      title: 'Brand',
      dataIndex: ['brand', 'name'],
      width: 160,
    },
    {
      title: 'UoM',
      dataIndex: ['uom', 'name'],
      width: 100,
    },
    {
      title: 'QTY',
      dataIndex: 'qty',
      width: 100,
      align: 'right',
      render: (value) => wtRoundValue(value || 0, 2),
    },
    {
      title: 'Unit Price',
      dataIndex: 'unit_price',
      width: 130,
      align: 'right',
      render: (value) => wtFormatRoundedValue(value || 0, '', '', 2),
    },
    {
      title: 'Total',
      dataIndex: 'total',
      width: 130,
      align: 'right',
      render: (value) => wtFormatRoundedValue(value || 0, '', '', 2),
    },
    {
      title: 'Tax Head',
      dataIndex: ['tax_head', 'name'],
      width: 160,
    },
    {
      title: 'Tax Rate',
      dataIndex: 'tax_rate',
      width: 100,
      align: 'right',
      render: (value) => wtRoundValue(value || 0, 2),
    },
    {
      title: 'Tax amount',
      dataIndex: 'tax_amount',
      width: 130,
      align: 'right',
      render: (value) => wtFormatRoundedValue(value || 0, '', '', 2),
    },
    {
      title: 'Net Total',
      dataIndex: 'net_total',
      width: 140,
      align: 'right',
      render: (value) => wtFormatRoundedValue(value || 0, '', '', 2),
    },
  ]

  const { onDownload } = useDownloadExcel({
    currentTableRef: tableRef.current,
    filename: 'GRN Report',
    sheet: 'GRN Report',
  })

  return (
    <div
      className="container-fluid"
      style={{ padding: '1em', backgroundColor: 'white', minHeight: '100vh' }}
    >
      <Form layout="vertical" form={form} onFinish={fetchReport}>
        <Card title={<span style={{ fontSize: 24 }}>GRN Report</span>}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={5}>
              <label>Date Range *</label>
              <DatePicker.RangePicker
                value={dateRange}
                onChange={setDateRange}
                style={{ width: '100%', height: 35 }}
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <label>Branch</label>
              <WidgetBranchSelector
                branches={branches}
                value={branchId}
                displayValue={branchName}
                onChange={(value) => {
                  setBranchId(value?.value || 0)
                  setBranchName(value?.label || '')
                }}
                showCloudIcon={false}
              />
            </Col>
            <Col xs={24} sm={12} md={5}>
              <label>Supplier</label>
              <WidgetVendorSelector
                vendors={vendors}
                value={vendorId}
                displayValue={vendorName}
                onChange={(value) => {
                  setVendorId(value?.value || 0)
                  setVendorName(value?.label || '')
                }}
                reloadDropdownData={() => Base.dropdownData('vendors', {}, [setVendors], 1)}
                showCloudIcon={false}
              />
            </Col>
            <Col xs={24} sm={12} md={5}>
              <label>Project</label>
              <WidgetProjectSelector
                data={projects}
                value={projectId}
                displayValue={projectName}
                onChange={(value) => {
                  setProjectId(value || 0)
                  const selected = projects.find((project) => project.value === value)
                  setProjectName(selected?.title || '')
                }}
                reloadDropdownData={() => Base.dropdownData('projects', {}, [setProjects], 1)}
                showCloudIcon={false}
              />
            </Col>
            <Col xs={24} sm={12} md={5}>
              <label>Item</label>
              <WidgetItemSelector
                items={items}
                value={itemId}
                displayValue={itemName}
                onChange={(value) => {
                  setItemId(value?.value || 0)
                  setItemName(value?.label || '')
                }}
                reloadDropdownData={() => Base.dropdownData('items', {}, [setItems], 1)}
                placeHolder="Select Item"
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <label>Brand</label>
              <WidgetItemBrandSelector
                item_brands={brands}
                value={brandId}
                displayValue={brandName}
                onChange={(value) => {
                  setBrandId(value?.value || 0)
                  setBrandName(value?.label || '')
                }}
                reloadDropdownData={() => Base.dropdownData('item_brands', {}, [setBrands], 1)}
                placeHolder="Select Brand"
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Button type="primary" htmlType="submit" style={{ marginTop: 30, marginRight: 8 }}>
                Search
              </Button>
              <Button
                style={{ marginTop: 30, backgroundColor: '#4BB543', color: '#fff' }}
                onClick={onDownload}
                disabled={!reportData.length}
              >
                Export
              </Button>
            </Col>
          </Row>
        </Card>
      </Form>

      <Card style={{ marginTop: 24 }}>
        <Spin spinning={isLoading}>
          <Table
            columns={columns}
            dataSource={reportData}
            rowKey="id"
            bordered
            pagination={false}
            scroll={{ x: 2400, y: 550 }}
          />
        </Spin>
      </Card>

      <table ref={tableRef} style={{ display: 'none' }}>
        <thead>
          <tr>
            <th>Branch</th>
            <th>GR No</th>
            <th>PO No</th>
            <th>LC Number</th>
            <th>GRN Date</th>
            <th>Supplier</th>
            <th>Project</th>
            <th>Item Name</th>
            <th>Brand</th>
            <th>UoM</th>
            <th>QTY</th>
            <th>Unit Price</th>
            <th>Total</th>
            <th>Tax Head</th>
            <th>Tax Rate</th>
            <th>Tax amount</th>
            <th>Net Total</th>
          </tr>
        </thead>
        <tbody>
          {reportData.map((row) => (
            <tr key={row.id}>
              <td>{row.branch?.name || ''}</td>
              <td>{row.goods_received_note?.code || ''}</td>
              <td>{row.purchase_order?.code || ''}</td>
              <td>{row.purchase_order?.lc_no || ''}</td>
              <td>{row.goods_received_note?.received_date || ''}</td>
              <td>{row.supplier?.name || ''}</td>
              <td>{row.project?.name || ''}</td>
              <td>{row.item?.name || ''}</td>
              <td>{row.brand?.name || ''}</td>
              <td>{row.uom?.name || ''}</td>
              <td>{wtFormatRoundedValue(row.qty || 0, '', '', 2)}</td>
              <td>{wtFormatRoundedValue(row.unit_price || 0, '', '', 2)}</td>
              <td>{wtFormatRoundedValue(row.total || 0, '', '', 2)}</td>
              <td>{row.tax_head?.name || ''}</td>
              <td>{wtFormatRoundedValue(row.tax_rate || 0, '', '', 2)}</td>
              <td>{wtFormatRoundedValue(row.tax_amount || 0, '', '', 2)}</td>
              <td>{wtFormatRoundedValue(row.net_total || 0, '', '', 2)}</td>
            </tr>
          ))}
          {reportData.length > 0 && (
            <tr>
              <td colSpan="11" style={{ fontWeight: 'bold', textAlign: 'right' }}>
                Total
              </td>
              <td style={{ fontWeight: 'bold' }}>
                {wtFormatRoundedValue(totals.total || 0, '', '', 2)}
              </td>
              <td></td>
              <td></td>
              <td style={{ fontWeight: 'bold' }}>
                {wtFormatRoundedValue(totals.tax_amount || 0, '', '', 2)}
              </td>
              <td style={{ fontWeight: 'bold' }}>
                {wtFormatRoundedValue(totals.net_total || 0, '', '', 2)}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

export default GrnReport
